import { useTranslation } from "react-i18next";
import { Card, CardDescription, Icon, Popup } from "semantic-ui-react";

import "components/CustomerCard/CustomerCard.scss";

import type { Customer } from "models";

type CustomerCardProps = {
  owner?: Customer;
  driver?: Customer;
  contractor?: Customer;
  ownerOptedOutFromImporting?: boolean;
  driverOptedOutFromImporting?: boolean;
  contractorOptedOutFromImporting?: boolean;
};

type RenderPersonCardProps = {
  title: "contractor" | "driver" | "owner";
  person?: Customer;
  ownerOptedOutFromImporting?: boolean;
  driverOptedOutFromImporting?: boolean;
  contractorOptedOutFromImporting?: boolean;
};

type LeaseCompanyTagProps = {
  isLeaseCompany: boolean;
};

const LeaseCompanyTag = ({ isLeaseCompany }: LeaseCompanyTagProps) => {
  const { t } = useTranslation();

  if (!isLeaseCompany) return null;

  return (
    <CardDescription
      content={
        <span>
          <Icon className="building" />
          {t("v8_lease_company", "Lease Company")}
        </span>
      }
    />
  );
};

const PersonCard = ({ person, title, driverOptedOutFromImporting, ownerOptedOutFromImporting, contractorOptedOutFromImporting }: RenderPersonCardProps) => {
  const { t } = useTranslation();

  if (!person) return null;

  const isCustomerOptedOut = driverOptedOutFromImporting || ownerOptedOutFromImporting || contractorOptedOutFromImporting;

  const { surname, is_lease_company, tel_private_nr, tel_business_nr, tel_mobile_private, tel_mobile_business, email_private, email_business } = person;

  return (
    <Card className={`CustomerCard__customer-card-no-box-shadow ${isCustomerOptedOut ? "-no-margin" : ""}`}>
      <Card.Content>
        <Card.Header content={isCustomerOptedOut ? t("v8_customer_opted_out", "Customer opted out") : surname} />
        <Card.Meta content={t(`v8_${title}`) || title} />
        {!isCustomerOptedOut && (
          <>
            <LeaseCompanyTag isLeaseCompany={is_lease_company} />
            {tel_private_nr && <Card.Description content={`${t("v8_private_phone", "Private phone")}: ${tel_private_nr}`} />}
            {tel_business_nr && <Card.Description content={`${t("v8_business_phone", "Business phone")}: ${tel_business_nr}`} />}
            {tel_mobile_private && <Card.Description content={`${t("v8_private_mobile", "Private mobile")}: ${tel_mobile_private}`} />}
            {tel_mobile_business && <Card.Description content={`${t("v8_business_mobile", "Business mobile")}: ${tel_mobile_business}`} />}
            {email_private && <Card.Description content={`${t("v8_private_email", "Private email")}: ${email_private}`} />}
            {email_business && <Card.Description content={`${t("v8_business_email", "Business email")}: ${email_business}`} />}
          </>
        )}
      </Card.Content>
    </Card>
  );
};

export const CustomerCard = ({
  contractor,
  driver,
  owner,
  driverOptedOutFromImporting,
  ownerOptedOutFromImporting,
  contractorOptedOutFromImporting
}: CustomerCardProps) => {
  const { t } = useTranslation();

  return (
    <Popup
      on="hover"
      position="bottom left"
      content={
        driver || owner || contractor ? (
          <>
            <PersonCard person={contractor} contractorOptedOutFromImporting={contractorOptedOutFromImporting} title="contractor" />
            <PersonCard person={driver} driverOptedOutFromImporting={driverOptedOutFromImporting} title="driver" />
            <PersonCard person={owner} ownerOptedOutFromImporting={ownerOptedOutFromImporting} title="owner" />
          </>
        ) : (
          <span>{t("v8_unknown_customer", "Unknown customer")}</span>
        )
      }
      trigger={
        <div className="CustomerCard__customer-card-container">
          <div className="CustomerCard__customer-card-user">
            <Icon className="user CustomerCard__customer-card-icon" />
          </div>
        </div>
      }
    />
  );
};
